import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Instagram, Send } from 'lucide-react';
import { scrollToSection } from '../utils/scrollToSection';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../mock';

const Contact = () => {
  const navigate = useNavigate();

  const handleScrollToSection = (path, sectionId) => {
    if (window.location.pathname === path) {
      scrollToSection(sectionId);
    } else {
      navigate(path);
      setTimeout(() => {
        if (path === '/donate') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          scrollToSection(sectionId);
        }
      }, 500);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Get in Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Have questions or want to learn more about our work? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Contact Information
                </h2>
                <p className="text-lg text-gray-600 mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Reach out to us through any of the following channels. We typically respond within 24-48 hours.
                </p>
              </div>

              <div className="space-y-6">
                {/* Email */}
                <Card className="border-2 border-blue-100 hover:border-blue-300 transition-colors shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-blue-600 p-3 rounded-lg">
                        <Mail className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          Email
                        </h3>
                        <a
                          href={`mailto:${mockData.contact.email}`}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                          style={{ fontFamily: 'Open Sans, sans-serif' }}
                        >
                          {mockData.contact.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Instagram */}
                <Card className="border-2 border-pink-100 hover:border-pink-300 transition-colors shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-3 rounded-lg">
                        <Instagram className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg text-gray-900 mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          Instagram
                        </h3>
                        <a
                          href={mockData.contact.instagramUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-pink-600 hover:text-pink-700 transition-colors"
                          style={{ fontFamily: 'Open Sans, sans-serif' }}
                        >
                          {mockData.contact.instagram}
                        </a>
                        <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                          Follow us for updates on our missions
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Additional Info Card */}
              <Card className="bg-blue-600 text-white border-none shadow-lg">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Join Our Mission
                  </h3>
                  <p className="text-blue-100 mb-6" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Whether you want to volunteer, partner with us, or simply learn more about our work, we're here to help.
                  </p>
                  <div className="space-y-2 text-sm text-blue-100" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    <p>✓ Volunteer opportunities</p>
                    <p>✓ Partnership inquiries</p>
                    <p>✓ General questions</p>
                    <p>✓ Media requests</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div id="contact-form">
              <Card className="border-none shadow-xl">
                <CardHeader>
                  <CardTitle className="text-3xl font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Send Us a Message
                  </CardTitle>
                  <CardDescription className="text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Click the button below to open your email client and send us a message
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div className="bg-blue-50 p-8 rounded-lg text-center border-2 border-blue-100">
                      <Mail className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Email Us Directly
                      </h3>
                      <p className="text-gray-600 mb-6" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        Click the button below to compose an email to us
                      </p>
                      <a href="mailto:info@medicsonmission.org">
                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-lg transition-all transform hover:scale-105"
                        >
                          <Send className="mr-2 h-5 w-5" />
                          Send message
                        </Button>
                      </a>
                    </div>
                    
                    <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                      <h4 className="font-bold text-lg text-gray-900 mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        What to include in your email:
                      </h4>
                      <ul className="space-y-2 text-gray-600" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span>Your full name and contact information</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span>Subject line describing your inquiry</span>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span>Detailed message about how we can help you</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Ready to Make a Difference?
          </h2>
          <p className="text-xl text-gray-600 mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Join us in bringing healthcare to communities around the world
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => handleScrollToSection('/donate', 'donation-form')}
              className="bg-red-500 hover:bg-red-600 text-white text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105"
            >
              Donate Now
            </Button>
            <Button 
              onClick={() => handleScrollToSection('/get-involved', 'volunteer-form')}
              variant="outline" 
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 text-lg px-10 py-6 rounded-lg transition-all transform hover:scale-105"
            >
              Become a Volunteer
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;